import type { SidebarSectionComponent } from "@productlane/api/src/browser"
import { cn } from "@productlane/lib/src/cn"

import { ActionButtonComponent } from "./action-button-component"
import { BadgeComponent } from "./badge-component"
import { CopyButtonComponent } from "./copy-button-component"
import { CopyFieldComponent } from "./copy-field-component"
import { DividerComponent } from "./divider-component"
import { LinkButtonComponent } from "./link-button-component"
import { RowComponent } from "./row-component"
import { SpacerComponent } from "./spacer-component"
import { TextComponent } from "./text-component"

interface Props {
	component: SidebarSectionComponent
	/** Margin above this item when stacking content against content. */
	gapBefore?: boolean
	/** Set inside a row, where each side sizes to its own content. */
	inRow?: boolean
}

function isSpacingComponent(component: SidebarSectionComponent) {
	return component.type === "spacer" || component.type === "divider"
}

const FULL_WIDTH_TYPES = new Set<SidebarSectionComponent["type"]>([
	"row",
	"text",
	"copyField",
	"linkButton",
	"actionButton",
])

function renderComponent(component: SidebarSectionComponent, inRow: boolean) {
	if (component.type === "badge") {
		return <BadgeComponent component={component} />
	}
	if (component.type === "text") {
		return <TextComponent component={component} />
	}
	if (component.type === "divider") {
		return <DividerComponent component={component} />
	}
	if (component.type === "spacer") {
		return <SpacerComponent component={component} />
	}
	if (component.type === "linkButton") {
		return <LinkButtonComponent component={component} fullWidth={!inRow} />
	}
	if (component.type === "copyButton") {
		return <CopyButtonComponent component={component} />
	}
	if (component.type === "copyField") {
		return <CopyFieldComponent component={component} />
	}
	if (component.type === "actionButton") {
		return <ActionButtonComponent component={component} fullWidth={!inRow} />
	}
	if (component.type === "row") {
		return <RowComponent component={component} />
	}
	return null
}

export function BaseComponent({
	component,
	gapBefore = false,
	inRow = false,
}: Props) {
	const node = renderComponent(component, inRow)
	if (!node) return null

	// Spacers/dividers own their vertical rhythm - do not add list gap around them.
	if (isSpacingComponent(component)) {
		return node
	}

	return (
		<div
			className={cn(
				!inRow && FULL_WIDTH_TYPES.has(component.type)
					? "w-full"
					: "w-fit max-w-full",
				gapBefore && "mt-1",
			)}
		>
			{node}
		</div>
	)
}

export { isSpacingComponent }
